import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { batchService } from "/app/src/services";
import { useTranslation } from "react-i18next";
import { Batch as BatchType } from "/app/src/models";
import { PowerPickTimeZoneToLocal } from "/app/src/components/generic/formatting/dateTime";
import { useQuery } from "@tanstack/react-query";
import { useHandleNavigationError } from "/app/src/helpers/api";
import Breadcrumbs from "/app/src/components/generic/components/breadcrumbs";
import InfoList from "/app/src/components/generic/components/explorer/infoList";

/**
 * Batch component retrieves and displays detailed information for a single batch.
 * @returns {JSX.Element} The rendered Batch component.
 */
export default function Batch() {
  const { t } = useTranslation();
  const { batchId } = useParams();
  const handleNavigationError = useHandleNavigationError();

  //Get the single batch from URL
  const { data: batch } = useQuery({
    queryKey: ["batch", batchId],
    queryFn: () => {
      return batchService.getSingle(batchId).catch(() => {
        handleNavigationError();
      });
    },
    initialData: { batch: {} },
    retry: false,
    select: (data: { batch: BatchType }) => {
      return data.batch;
    },
  });

  /**
   * Map numeric priority to a translated label.
   * @param priority numeric priority
   * @returns translated string label or '-' when unknown
   */
  const getPriorityType = (priority: number | undefined) => {
    switch (priority) {
      case 1:
        return t("translation:low");
      case 2:
        return t("translation:medium");
      case 3:
        return t("translation:high");
      case 4:
        return t("translation:hot");
      default:
        return "-";
    }
  };

  const basicInfo = [
    { label: t("translation:name"), value: batch?.name },
    {
      label: t("translation:creation_date"),
      value: (
        <PowerPickTimeZoneToLocal
          date={batch?.creationDate}
          format={"MMM DD, YYYY HH:mm"}
        />
      ),
    },
    {
      label: t("translation:priority"),
      value: getPriorityType(batch?.priority),
    },
    { label: t("translation:type"), value: batch?.type },
    { label: t("translation:deadline"), value: batch?.deadline },
  ];

  return (
    <div className="explorer">
      <Helmet>
        <title>{batch?.name}</title>
      </Helmet>
      {batch?.name ? (
        <>
          <Breadcrumbs
            items={[
              { label: "Explorer", to: "/explorer" },
              { label: "Batches", to: "/explorer/batches" },
              { label: batch.name },
            ]}
          />
          <h1 className="text-[36px] font-medium">{batch?.name}</h1>
          <h2 className="text-[16px] mb-6 text-neutral-smoke">
            Batch Id: {batch?.id}
          </h2>
          <InfoList header={t("translation:basic_info")} items={basicInfo} />
        </>
      ) : (
        <div />
      )}
    </div>
  );
}
